import type { TFunction } from 'i18next';
import type { FactorDimension } from '../../api/hms';
import type {
  AssessedArea,
  DimensionSummary,
  Measurement,
  RiskQuestion,
  RiskResult,
  UnassessedArea,
} from '../../api/workEnvironmentTypes';
import { fullDate, joinList, monthAndYear } from '../HRDashboard/hmsFormat';
import {
  AGREEMENT_LIMITS,
  Agreement,
  agreement,
  areaName,
  assessedPlacement,
  byRisk,
  dimensionBalance,
  LEVEL_COLORS,
  oneDecimal,
  TagColor,
} from '../WorkEnvironmentSurveyDetail/riskView';
import { responsePercent } from '../WorkEnvironmentSurveyDetail/surveyProcessView';

/*
 * The results of one survey as a plain report: no requirements, no documentation.
 * The rules live in the risk step; what is here only writes the results out.
 */

const K = 'workEnvironmentReport';

type Dimensions = Record<FactorDimension, DimensionSummary | null>;

export const NO_DIMENSIONS: Dimensions = { LOAD: null, RESOURCE: null };
export type AssessedRisk = Extract<RiskResult, { status: 'assessed' }>;

const SCALE = { min: 1, max: 5 };
const MAX_SCORE = SCALE.max * SCALE.max;

/** «Gjennomført 21. september 2026 · 5 av 5 svarte (100 %)» */
export const reportMeta = (
  measuredAt: string,
  responses: number,
  expected: number,
  t: TFunction,
  language: string,
) =>
  t(`${K}.hero.meta`, {
    date: fullDate(measuredAt, language),
    responses,
    expected,
    percent: responsePercent(responses, expected),
  });

export type RoundTag = { key: string; label: string; color: TagColor | 'neutral' };

/** An evaluation measures an earlier round again, and may start the next one */
export const roundTags = (measurement: Measurement | undefined, t: TFunction): RoundTag[] =>
  measurement
    ? [
        ...(measurement.evaluatesRound !== null
          ? [
              {
                key: 'evaluates',
                label: t(`${K}.hero.evaluates`, { round: measurement.evaluatesRound }),
                color: 'neutral' as const,
              },
            ]
          : []),
        ...(measurement.round !== null
          ? [{ key: 'round', label: t(`${K}.hero.round`, { round: measurement.round }), color: 'info' as const }]
          : []),
      ]
    : [];

/** «september 2026 · runde 2», newest first as the API lists them */
export const measurementOptions = (measurements: Measurement[], t: TFunction, language: string) =>
  measurements.map((measurement) => ({
    value: String(measurement.surveyId),
    label: [
      monthAndYear(measurement.measuredAt, language),
      measurement.round !== null
        ? t(`${K}.hero.round`, { round: measurement.round }).toLowerCase()
        : t(`${K}.hero.evaluates`, { round: measurement.evaluatesRound }).toLowerCase(),
    ].join(' · '),
  }));

/** The measurement before this one, to show how the team has moved since */
export const previousMeasurement = (measurements: Measurement[], surveyId: number) => {
  const index = measurements.findIndex((measurement) => measurement.surveyId === surveyId);
  return index >= 0 ? (measurements[index + 1] ?? null) : null;
};

export const widelySpread = (questions: RiskQuestion[]) =>
  questions.filter((question) => agreement(question.standardDeviation) === 'wide');

const allQuestions = (risk: AssessedRisk) => risk.areas.flatMap((area) => area.questions);

const assessedLevelOf = (area: AssessedArea) => assessedPlacement(area).level;

type Direction = 'up' | 'down' | 'same';

const direction = (from: number, to: number, language: string): Direction => {
  if (oneDecimal(from, language) === oneDecimal(to, language)) return 'same';
  return to > from ? 'up' : 'down';
};

const change = (dimension: FactorDimension, from: number, to: number, t: TFunction, language: string) =>
  t(`${K}.jdr.change.${dimension}.${direction(from, to, language)}`, {
    from: oneDecimal(from, language),
    to: oneDecimal(to, language),
  });

/** «Siden september 2025 har kravene gått ned (3,1 → 2,9) og ressursene opp (3,2 → 3,5).» */
export const movementText = (
  current: Dimensions,
  previous: { dimensions: Dimensions; measuredAt: string } | null,
  t: TFunction,
  language: string,
) => {
  const before = previous?.dimensions;
  if (!previous || !before?.LOAD || !before.RESOURCE || !current.LOAD || !current.RESOURCE) return null;
  return t(`${K}.jdr.movement`, {
    since: monthAndYear(previous.measuredAt, language),
    loads: change('LOAD', before.LOAD.average, current.LOAD.average, t, language),
    resources: change('RESOURCE', before.RESOURCE.average, current.RESOURCE.average, t, language),
  });
};

const weighs = (balance: number) => {
  if (balance > 0) return 'resources';
  return balance < 0 ? 'loads' : 'even';
};

/** Means, not sums, so the number of areas does not decide which side weighs most */
const balanceSentence = (dimensions: Dimensions, t: TFunction, language: string) => {
  const balance = dimensionBalance(dimensions);
  return balance === null || !dimensions.LOAD || !dimensions.RESOURCE
    ? ''
    : t(`${K}.summary.balance.${weighs(balance)}`, {
        loads: oneDecimal(dimensions.LOAD.average, language),
        resources: oneDecimal(dimensions.RESOURCE.average, language),
      });
};

/** The summary at the top: the balance, the strongest strain and resource, and what needs attention */
export const summaryText = (risk: AssessedRisk, dimensions: Dimensions, t: TFunction, language: string) => {
  const strain = dimensions.LOAD?.areas[0];
  const resource = dimensions.RESOURCE?.areas[0];
  const wide = widelySpread(allQuestions(risk)).length;
  const high = risk.areas.filter((area) => assessedLevelOf(area) === 'high').length;
  return [
    balanceSentence(dimensions, t, language),
    strain && t(`${K}.summary.strain`, { name: strain.name }),
    resource && t(`${K}.summary.resource`, { name: resource.name.toLowerCase() }),
    t(`${K}.summary.high`, { count: high, total: risk.areas.length }),
    t(`${K}.summary.${wide > 0 ? 'wide' : 'agreed'}`, { count: wide }),
  ]
    .filter(Boolean)
    .join(' ');
};

export type KeyFigure = { key: string; label: string; value: string; sub: string; tone: TagColor };

/** The response rate, high risk and wide disagreement. Green when there is nothing to look at. */
export const keyFigures = (risk: AssessedRisk, expected: number, t: TFunction): KeyFigure[] => {
  const high = risk.areas.filter((area) => assessedLevelOf(area) === 'high').length;
  const wide = widelySpread(allQuestions(risk)).length;
  return [
    {
      key: 'responses',
      label: t(`${K}.figures.responses`),
      value: t(`${K}.figures.percent`, { percent: responsePercent(risk.responses, expected) }),
      sub: t(`${K}.figures.answered`, { responses: risk.responses, expected }),
      tone: 'success',
    },
    {
      key: 'high',
      label: t(`${K}.figures.high`),
      value: t(`${K}.figures.ofTotal`, { count: high, total: risk.areas.length }),
      sub: t(`${K}.figures.areas`),
      tone: high > 0 ? 'danger' : 'success',
    },
    {
      key: 'wide',
      label: t(`${K}.figures.wide`),
      value: String(wide),
      sub: t(`${K}.figures.questions`, { count: wide }),
      tone: wide > 0 ? 'warning' : 'success',
    },
  ];
};

/** «Ressursene veier tyngst», under the balance */
export const balanceNote = (balance: number, t: TFunction) => t(`${K}.jdr.weighs.${weighs(balance)}`);

/** «Høy», as the list shows it */
const levelLabel = (area: AssessedArea, t: TFunction) => t(`hrRiskMatrix.levelNames.${assessedLevelOf(area)}`);

/** Areas are numbered highest risk first, so the matrix and the questions can refer to them */
export const numberedAreas = (areas: AssessedArea[]) =>
  byRisk(areas).map((area, index) => ({ area, number: index + 1 }));

/** Highest risk first, with «Høy · 25» and a bar for the score out of 25 */
export const riskRows = (areas: AssessedArea[], t: TFunction) =>
  numberedAreas(areas).map(({ area }) => {
    const placement = assessedPlacement(area);
    return {
      key: area.key,
      name: areaName(area, t),
      tag: t(`${K}.risk.tag`, { level: levelLabel(area, t), score: placement.score }),
      adjusted: area.adjustment !== null,
      width: `${(placement.score / MAX_SCORE) * 100}%`,
      color: LEVEL_COLORS[placement.level],
    };
  });

export const unassessedText = (unassessed: UnassessedArea[], t: TFunction) =>
  unassessed.length > 0
    ? t(`${K}.risk.unassessed`, { names: joinList(unassessed.map((area) => areaName(area, t)), t, 'and') })
    : null;

const percentOf = (value: number) =>
  ((Math.min(Math.max(value, SCALE.min), SCALE.max) - SCALE.min) / (SCALE.max - SCALE.min)) * 100;

/** The mean of a question, with one standard deviation to each side, kept within 1–5 */
export const spreadBar = (mean: number, standardDeviation: number) => {
  const from = percentOf(mean - standardDeviation);
  const to = percentOf(mean + standardDeviation);
  return { from: `${from}%`, width: `${to - from}%`, mean: `${percentOf(mean)}%` };
};

export type QuestionFilter = 'all' | FactorDimension | 'wide';

export const QUESTION_FILTERS: QuestionFilter[] = ['all', 'LOAD', 'RESOURCE', 'wide'];

export const AGREEMENTS: Agreement[] = ['same', 'some', 'wide'];

export type QuestionArea = {
  key: string;
  number: number;
  name: string;
  description: string;
  dimension: FactorDimension;
  type: string;
  level: string;
  color: TagColor;
  wide: boolean;
  score: string;
  questions: Array<{
    ref: string;
    text: string;
    isIncident: boolean;
    figures: string;
    agreement: Agreement;
    bar: ReturnType<typeof spreadBar>;
  }>;
};

const typeOf = (area: AssessedArea, t: TFunction) =>
  t(`${K}.questions.types.${area.sensitive ? 'sensitive' : area.dimension}`);

export const questionAreas = (areas: AssessedArea[], t: TFunction, language: string): QuestionArea[] =>
  numberedAreas(areas).map(({ area, number }) => ({
    key: area.key,
    number,
    name: areaName(area, t),
    description: area.description,
    dimension: area.dimension,
    type: typeOf(area, t),
    level: t(`${K}.questions.levels.${assessedLevelOf(area)}`),
    color: LEVEL_COLORS[assessedLevelOf(area)],
    wide: widelySpread(area.questions).length > 0,
    score: t(`${K}.questions.score`, {
      score: oneDecimal(area.dimensionScore, language),
      count: area.questions.length,
    }),
    questions: area.questions.map((question) => {
      const degree = agreement(question.standardDeviation);
      return {
        ref: question.ref,
        text: question.text,
        isIncident: question.isIncident === true,
        figures: t(`${K}.questions.figures`, {
          mean: oneDecimal(question.mean, language),
          spread: oneDecimal(question.standardDeviation, language),
          agreement: t(`${K}.agreement.${degree}`).toLowerCase(),
        }),
        agreement: degree,
        bar: spreadBar(question.mean, question.standardDeviation),
      };
    }),
  }));

const FILTERS: Record<QuestionFilter, (area: QuestionArea) => boolean> = {
  all: () => true,
  LOAD: (area) => area.dimension === 'LOAD',
  RESOURCE: (area) => area.dimension === 'RESOURCE',
  wide: (area) => area.wide,
};

export const filterQuestionAreas = (areas: QuestionArea[], filter: QuestionFilter) => areas.filter(FILTERS[filter]);

/** Areas where the team disagrees a lot are open from the start, as they need a closer look */
export const openFromStart = (areas: QuestionArea[]) => areas.filter((area) => area.wide).map((area) => area.key);

export const methodItems = (risk: Pick<AssessedRisk, 'levels' | 'minResponses'>, t: TFunction, language: string) => [
  { key: 'scale', title: t(`${K}.method.scale.title`), text: t(`${K}.method.scale.text`) },
  {
    key: 'spread',
    title: t(`${K}.method.spread.title`),
    text: t(`${K}.method.spread.text`, {
      same: oneDecimal(AGREEMENT_LIMITS.same, language),
      some: oneDecimal(AGREEMENT_LIMITS.some, language),
    }),
  },
  {
    key: 'risk',
    title: t(`${K}.method.risk.title`),
    text: t(`${K}.method.risk.text`, {
      high: risk.levels.highFrom,
      moderate: risk.levels.moderateFrom,
      moderateTo: risk.levels.highFrom - 1,
    }),
  },
  {
    key: 'incidents',
    title: t(`${K}.method.incidents.title`),
    text: t(`${K}.method.incidents.text`),
  },
  {
    key: 'anonymity',
    title: t(`${K}.method.anonymity.title`),
    text: t(`${K}.method.anonymity.text`, { count: risk.minResponses }),
  },
  {
    key: 'about',
    title: t(`${K}.method.about.title`),
    text: t(`${K}.method.about.text`),
  },
];

export type Section = 'summary' | 'jdr' | 'risk' | 'questions' | 'method';
